/**
 * Pipeline Bottleneck Radar — the judgement layer.
 *
 * The proxy ships raw measures; everything here decides what colour they are. Kept out of the
 * component and out of the server for the same reason: these thresholds are the part TA will argue
 * about and change, and they need to be testable without a browser or a warehouse.
 *
 * ── The rule, unchanged from the mockup this replaces ─────────────────────────────────────────
 * Four phases, a traffic light each. A phase is as bad as its worst signal, except phase 1, which
 * is blended (see `stageInflow`). The role's overall light is a weighted average rather than the
 * worst phase, so one weak phase in an otherwise healthy role does not read as a crisis. The
 * bottleneck is the FIRST red, else the first yellow — first, not worst, because a funnel is a
 * sequence and fixing a late phase while an early one starves it achieves nothing.
 *
 * ── The one addition live data forced: `unknown` ──────────────────────────────────────────────
 * The mockup was typed by hand, so every figure existed. Live, they often do not: a role opened
 * last week has no mature cohort, a role with three case interviews this quarter has a conversion
 * rate that is noise, and a posting Lever no longer publishes has nothing at all. The mockup's
 * arithmetic turned all three into GREEN — `expected > 0 ? actual / expected : 1` returns a passing
 * ratio when there is no expectation to measure against, and a 100%-on-two-candidates rate sails
 * through every threshold.
 *
 * So a signal with too thin a cohort is `unknown`: it is shown with its `n`, it never colours a
 * phase, and it never becomes a bottleneck. A phase whose signals are all unknown is unknown, and
 * an unknown phase is left OUT of the overall average rather than scored as half a pass — a role we
 * cannot yet judge must not dilute a role we can. This is the only place the logic departs from the
 * mockup, and it departs towards saying nothing rather than saying "fine".
 */
import type {
  BottleneckRadarData,
  RadarBenchmarks,
  RadarMeasures,
  RadarPosition,
} from '../types/taBottleneck';

import {
  MIN_COHORT,
  PIPELINE_MULTIPLIER,
  RAMP_WEEKS,
  TARGET_FILL_WEEKS,
  conversionSignal,
  days,
  fractionStatus,
  num,
  pct,
  worst,
} from './bottleneckSignals';
import type { RoleEvaluation, Signal, Stage, StageStatus } from './bottleneckSignals';

export {
  MIN_COHORT,
  PIPELINE_MULTIPLIER,
  RAMP_WEEKS,
  TARGET_FILL_WEEKS,
  conversionSignal,
  fractionStatus,
  ratioStatus,
  worst,
} from './bottleneckSignals';
export type {
  ConversionFallback,
  RoleEvaluation,
  Signal,
  SignalStatus,
  Stage,
  StageStatus,
} from './bottleneckSignals';


/**
 * Phase 1 is BLENDED rather than worst-of, and deliberately so: a full pipeline with a weak
 * conversion is still a full pipeline, and calling it red sends a recruiter to fix sourcing when
 * sourcing is not the problem. Conversion only drags phase 1 down once the pipeline is thin.
 */
export function stageInflow(m: RadarMeasures, openHires: number, bench: RadarBenchmarks): Stage {
  const needed = Math.max(1, openHires * PIPELINE_MULTIPLIER);
  const coverage = m.activeQualified / needed;
  const cov = fractionStatus(coverage);
  /**
   * ⚠️ NO THIN-COHORT FALLBACK HERE, unlike phases 2 and 3. Phase 1 runs on the applicant slice and
   * applications are plentiful: widening that span moved a role's rate by up to 14.7 points and
   * made not one extra role judgeable. A fallback that buys no judgement and changes the number is
   * a worse figure, not a wider one.
   */
  const conv = conversionSignal('Conversion', m.conv1, bench.conv1, 'Applicant → first interview');

  let status: StageStatus;
  if (cov === 'green') status = 'green';
  else if (cov === 'yellow') status = conv.status === 'green' ? 'green' : 'yellow';
  else if (cov === 'red') status = conv.status === 'green' ? 'yellow' : 'red';
  else status = conv.status === 'info' ? 'unknown' : (conv.status as StageStatus);

  return {
    key: 's1',
    name: 'Inflow & Pipeline',
    status,
    signals: [
      {
        key: 'coverage',
        label: 'Pipeline coverage',
        value: `${Math.round(coverage * 100)}%`,
        sub: `${m.activeQualified} qualified / ${needed} wanted`,
        status: cov,
      },
      {
        label: 'Time to first interview',
        value: days(m.daysToFirstInterview),
        sub: 'Application → moved to FI',
        status: 'info',
      },
      { ...conv, key: 'conv1' },
      {
        label: 'Inflow / week',
        value: num(m.inflowPerWeek),
        sub: 'newly qualified',
        status: 'info',
      },
    ],
  };
}

/**
 * What a role has to interview per week to land its open hires inside `TARGET_FILL_WEEKS`, worked
 * backwards through FINN's own conversion rates.
 *
 * The BENCHMARK rates are used, never the role's own: a target built on a role's actual conversion
 * moves every time that conversion moves, so a role that converts badly would quietly be asked for
 * fewer interviews and its throughput would stay green all the way down. The target is a plan.
 */
export function interviewTargets(
  openHires: number,
  bench: RadarBenchmarks
): { s2: number | null; s3: number | null } {
  const accept = bench.accept && bench.accept > 0 ? bench.accept / 100 : null;
  const c3 = bench.conv3 && bench.conv3 > 0 ? bench.conv3 / 100 : null;
  const c2 = bench.conv2 && bench.conv2 > 0 ? bench.conv2 / 100 : null;
  if (!accept || !c3 || !c2) return { s2: null, s3: null };

  const offers = openHires / accept;
  const s3Total = offers / c3;
  const s2Total = s3Total / c2;
  return {
    s3: Math.round((s3Total / TARGET_FILL_WEEKS) * 10) / 10,
    s2: Math.round((s2Total / TARGET_FILL_WEEKS) * 10) / 10,
  };
}

function throughputSignal(perWeek: number, target: number | null): Signal {
  const fraction = target && target > 0 ? perWeek / target : null;
  return {
    label: 'Interview throughput',
    value: fraction == null ? '–' : `${Math.round(fraction * 100)}%`,
    sub: `${perWeek}/wk · target ${target == null ? '–' : target}/wk`,
    status: fractionStatus(fraction),
  };
}

export function stagePersonal(
  m: RadarMeasures,
  target: number | null,
  bench: RadarBenchmarks,
  windowNote?: string
): Stage {
  const tp = throughputSignal(m.s2InterviewsPerWeek, target);
  const conv = conversionSignal('Conversion', m.conv2, bench.conv2, 'First interview → case', windowNote, {
    actual: m.conv2Ytd,
    expected: bench.conv2Ytd,
    note: 'year to date',
  });
  return {
    key: 's2',
    name: 'Pre / Personal Interview',
    status: worst([tp.status, conv.status]),
    signals: [
      { ...tp, key: 'throughput2' },
      { ...conv, key: 'conv2' },
      {
        label: 'Time in stage',
        value: days(m.s2StageDays),
        sub: 'First interview → case',
        status: 'info',
      },
    ],
  };
}

export function stageCase(
  m: RadarMeasures,
  target: number | null,
  bench: RadarBenchmarks,
  windowNote?: string
): Stage {
  const tp = throughputSignal(m.s3InterviewsPerWeek, target);
  const conv = conversionSignal('Conversion', m.conv3, bench.conv3, 'Case → offer', windowNote, {
    actual: m.conv3Ytd,
    expected: bench.conv3Ytd,
    note: 'year to date',
  });
  return {
    key: 's3',
    name: 'Case & Final Interview',
    status: worst([tp.status, conv.status]),
    signals: [
      { ...tp, key: 'throughput3' },
      { ...conv, key: 'conv3' },
      { label: 'Time in stage', value: days(m.s3StageDays), sub: 'Case → offer', status: 'info' },
      {
        label: 'Feedback time',
        value: days(m.feedbackDays),
        sub: 'interview → feedback',
        status: 'info',
      },
    ],
  };
}

/**
 * Offer acceptance is the only signal here that drives the light. Decision time is measured and
 * shown but stays informational, in line with every other duration — see `TIME_REFERENCE`.
 *
 * ── Why this one signal widens its own window ────────────────────────────────────────────────
 * Offers are rare per role: a quarter rarely holds ten of them, and every other conversion here
 * refuses to judge under `MIN_COHORT`. Holding this one to the quarter alone would make phase 4
 * grey on nearly every role; holding it to the quarter and judging anyway would put a light on a
 * cohort of three. So it falls back to the YEAR when the quarter is too thin — the same
 * "<10 cases → YTD" rule the mockup's own notes prescribed — and only goes grey when the year is
 * thin too. The sub-label always says which window and how many, so a figure can be argued with.
 */
export function offerAcceptance(m: RadarMeasures): Signal {
  const quarterEnough = m.offersOut >= MIN_COHORT;
  const resolved = quarterEnough ? m.offersOut : m.offersOutYtd;
  const accepted = quarterEnough ? m.offersAccepted : m.offersAcceptedYtd;
  const window = quarterEnough ? 'last quarter' : 'year to date';

  if (resolved < MIN_COHORT) {
    return {
      key: 'acceptance',
      label: 'Offer acceptance',
      value: resolved > 0 ? pct((accepted / resolved) * 100) : '–',
      sub:
        resolved > 0
          ? `${accepted}/${resolved} accepted · only ${resolved} to judge on`
          : 'no offers resolved yet',
      status: 'unknown',
    };
  }

  const rate = (accepted / resolved) * 100;
  return {
    key: 'acceptance',
    label: 'Offer acceptance',
    value: pct(rate),
    sub: `${accepted}/${resolved} accepted · ${window}`,
    status: rate >= 80 ? 'green' : rate >= 60 ? 'yellow' : 'red',
  };
}

export function stageOffer(m: RadarMeasures): Stage {
  const oa = offerAcceptance(m);

  return {
    key: 's4',
    name: 'Decision & Offer',
    status: worst([oa.status]),
    signals: [
      {
        label: 'Decision time',
        value: days(m.decisionDays),
        sub: 'final interview → offer',
        status: 'info',
      },
      oa,
    ],
  };
}

/**
 * Reference thresholds for the duration signals, in days (green / yellow).
 *
 * These are NOT applied. The mockup switched every time signal to information-only because the
 * numbers behind them could not be trusted; the warehouse now supplies real medians over completed
 * transitions, so the reason is gone — but turning them on changes which roles read as broken, and
 * that is TA's call to make deliberately rather than a side effect of wiring up the data. Shown in
 * the legend so the figures on the cards can still be read against something.
 */
export const TIME_REFERENCE = {
  daysToFirstInterview: [3, 7],
  s2StageDays: [7, 14],
  s3StageDays: [10, 20],
  feedbackDays: [2, 4],
  decisionDays: [3, 7],
} as const;

/** "Last 6 months" reads better than "180 days" on a control somebody sets once a quarter. */
export function windowLabel(days: number): string {
  if (!days) return 'All history';
  // The proxy's sentinel for 1 January to today (THIS_YEAR_WINDOW).
  if (days === -1) return 'This year';
  if (days === 30) return 'Last month';
  if (days % 365 === 0) return days === 365 ? 'Last 12 months' : `Last ${days / 365} years`;
  if (days % 30 === 0) return `Last ${Math.round(days / 30)} months`;
  return `Last ${days} days`;
}

/**
 * The role's four phases, its overall light and its bottleneck.
 *
 * Overall is a weighted average of the phases we can actually judge: green 1, yellow 0.5, red 0,
 * unknown excluded. ≥0.7 green · ≥0.4 yellow · below that red. A role with nothing judgeable at all
 * is `unknown` rather than red — we have not found a problem, we have found no data.
 */
export function evaluate(
  position: RadarPosition,
  bench: RadarBenchmarks,
  /** What phases 2 and 3 were measured over, for the sub-labels. */
  windowNote?: string
): RoleEvaluation {
  const m = position.measures;
  if (!m) return { stages: [], overall: 'unknown', bottleneck: null, ramp: false };

  const targets = interviewTargets(position.openHires, bench);
  const raw: Stage[] = [
    stageInflow(m, position.openHires, bench),
    stagePersonal(m, targets.s2, bench, windowNote),
    stageCase(m, targets.s3, bench, windowNote),
    stageOffer(m),
  ];

  /**
   * ⚠️ An UNKNOWN age is not a new role. `weeksOpen ?? 0` read a missing posting date as "opened
   * today" and handed it the softening, which turns reds into ambers on precisely the roles we know
   * least about. Ramp-up forgives, so it must only fire on a date we actually have.
   */
  const ramp = position.weeksOpen != null && position.weeksOpen <= RAMP_WEEKS;
  const stages = raw.map((s) =>
    ramp && s.status === 'red' ? { ...s, status: 'yellow' as StageStatus, ramped: true } : s
  );

  const judged = stages.filter((s) => s.status !== 'unknown');
  let overall: StageStatus = 'unknown';
  if (judged.length) {
    const score =
      judged.reduce((a, s) => a + (s.status === 'green' ? 1 : s.status === 'yellow' ? 0.5 : 0), 0) /
      judged.length;
    overall = score >= 0.7 ? 'green' : score >= 0.4 ? 'yellow' : 'red';
  }

  const bottleneck =
    stages.find((s) => s.status === 'red') || stages.find((s) => s.status === 'yellow') || null;

  return { stages, overall, bottleneck, ramp };
}

export interface RadarSummary {
  counts: Record<StageStatus, number>;
  /** The phase blocking the most at-risk roles, and how many. */
  topBottleneck: { name: string; roles: number } | null;
  /** Roles that are not green, which is what `topBottleneck` is counted against. */
  problemRoles: number;
}

/** The KPI strip: how many roles sit at each light, and which phase is blocking the most of them. */
export function summarise(
  positions: RadarPosition[],
  bench: RadarBenchmarks
): RadarSummary {
  const counts: Record<StageStatus, number> = { green: 0, yellow: 0, red: 0, unknown: 0 };
  const byPhase = new Map<string, number>();
  let problemRoles = 0;

  positions.forEach((p) => {
    const ev = evaluate(p, bench);
    counts[ev.overall] += 1;
    if (ev.overall === 'green' || ev.overall === 'unknown') return;
    problemRoles += 1;
    if (ev.bottleneck) byPhase.set(ev.bottleneck.name, (byPhase.get(ev.bottleneck.name) || 0) + 1);
  });

  const top = [...byPhase.entries()].sort((a, b) => b[1] - a[1])[0];
  return {
    counts,
    topBottleneck: top ? { name: top[0], roles: top[1] } : null,
    problemRoles,
  };
}

/** Everything the tab renders, computed once. */
export function evaluateAll(data: BottleneckRadarData) {
  const rows = data.positions.map((position) => ({
    position,
    evaluation: evaluate(position, data.benchmarks),
  }));
  return { rows, summary: summarise(data.positions, data.benchmarks) };
}
